import { useMemo } from 'react';
import { LuCopy, LuPlus } from 'react-icons/lu';

import { Allocation, AllocationType, Strategy } from '@ndvr/portfolio-api';

import { Combobox } from '@/components/ui/combobox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCreateAllocation } from 'views/portfolio-engine/allocation-builder/allocations/allocation-queries';
import { EditAllocationDialog } from 'views/portfolio-engine/allocation-builder/allocations/edit-dialog/EditAllocationDialog';
import { StrategyAllocations } from 'views/portfolio-engine/allocation-builder/allocations/StrategyAllocations';

import { usePortfolioAllocationState } from './usePortfolioAllocationState';

interface PortfolioAllocationViewProps {
    cardIsEditing: boolean;
    portfolioAllocationGUID?: string;
    portfolioAllocation?: Allocation;
    allocations: Allocation[];
    isLoadingAllocations: boolean;
    isLoadingPermissions?: boolean;
    onPortfolioAllocationChange: (allocationGUID: string | undefined) => void;
    canCreateSharedAllocation?: boolean;
    availableStrategies: Strategy[];
}

export const PortfolioAllocationView = ({
    cardIsEditing,
    portfolioAllocationGUID,
    portfolioAllocation,
    allocations,
    isLoadingAllocations,
    isLoadingPermissions,
    onPortfolioAllocationChange,
    canCreateSharedAllocation,
    availableStrategies,
}: PortfolioAllocationViewProps) => {
    const { mutate: createAllocation, isPending: isSaving } = useCreateAllocation();

    const {
        allocationTypeFilter,
        isDialogOpen,
        editingAllocation,
        handleAllocationTypeFilterChange,
        handleCreateAllocation,
        handleCloneAllocation,
        handleDialogClose,
        handleAllocationDialogSave,
    } = usePortfolioAllocationState({
        cardIsEditing,
        portfolioAllocation,
        allocations,
        onPortfolioAllocationChange,
        createAllocation,
        canCreateSharedAllocation,
    });

    const filteredAllocations = useMemo(
        () => allocations.filter((allocation) => allocation.allocationType === allocationTypeFilter),
        [allocations, allocationTypeFilter],
    );

    return (
        <div className="space-y-4">
            {cardIsEditing && (
                <Tabs
                    value={allocationTypeFilter}
                    onValueChange={(value) => handleAllocationTypeFilterChange(value as AllocationType)}
                >
                    <TabsList className="border-none">
                        <TabsTrigger value={AllocationType.SHARED}>Shared</TabsTrigger>
                        <TabsTrigger value={AllocationType.PERSONAL}>My Allocations</TabsTrigger>
                    </TabsList>
                </Tabs>
            )}
            <div>
                <label className="mb-2 block text-sm font-medium">Portfolio Allocation</label>
                {cardIsEditing ? (
                    <div className="flex gap-2">
                        <Combobox
                            options={filteredAllocations.map((a) => ({
                                value: a.allocationGUID,
                                label: a.allocationName,
                            }))}
                            value={portfolioAllocationGUID}
                            onValueChange={(value) => onPortfolioAllocationChange(value || undefined)}
                            placeholder={isLoadingAllocations ? 'Loading allocations...' : 'Select an allocation'}
                            disabled={isLoadingAllocations}
                            className="min-w-0 flex-1"
                        />
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={handleCreateAllocation}
                                        disabled={isLoadingAllocations}
                                        className="shrink-0 rounded border border-gray-300 p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <LuPlus size={20} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Create new allocation</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => handleCloneAllocation(portfolioAllocationGUID)}
                                        disabled={
                                            !portfolioAllocationGUID || isLoadingAllocations || isLoadingPermissions
                                        }
                                        className="shrink-0 rounded border border-gray-300 p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <LuCopy size={20} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {isLoadingPermissions
                                        ? 'Loading permissions…'
                                        : portfolioAllocationGUID
                                          ? 'Clone current allocation'
                                          : 'Select an allocation to clone'}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                ) : portfolioAllocation ? (
                    <div className="space-y-2">
                        <div className="rounded-md border border-gray-300 bg-gray-50 p-3">
                            {portfolioAllocation.allocationName}
                        </div>
                        <StrategyAllocations strategyAllocations={portfolioAllocation.strategyAllocations} />
                    </div>
                ) : (
                    <div className="rounded-md border border-gray-300 bg-gray-50 p-3">No allocation</div>
                )}
            </div>
            <EditAllocationDialog
                open={isDialogOpen}
                allocation={editingAllocation}
                onSave={handleAllocationDialogSave}
                onCancel={handleDialogClose}
                isSaving={isSaving}
                canCreateSharedAllocation={canCreateSharedAllocation}
                existingAllocationNames={allocations.map((a) => a.allocationName)}
                availableStrategies={availableStrategies}
            />
        </div>
    );
};
